/**
 * OmniRetail POS — Full Cashier UX per spec
 * Top bar | Left: search+categories+product grid | Right: cart+payment | Bottom: shortcut bar
 * Modals: EditLine, CartDiscount, PriceOverride, ReturnRefund, EndShift, Shortcuts, Receipt
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import axios from 'axios';
import { posApi, authApi, salesApi, inventoryApi } from '../../api';
import { getErrorMessage, apiBaseUrl } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { useCurrency } from '../../hooks/useCurrency';
import { fmtCurrency as fmt } from '../../utils/currency';

interface Product { id:string;sku:string;barcode:string|null;name:string;base_price:string|number;tax_rate:string|number;available_qty?:string|number;category_id?:string|null; }
interface CartLine { productId:string;sku:string;name:string;unitPrice:number;originalPrice:number;quantity:number;discountPercent:number;taxRate:number; }
interface PaymentInput { method:'cash'|'card'|'wallet'|'bank_transfer'|'gift_card'|'store_credit';amount:number;reference?:string;gatewayTxnId?:string; }

function toNum(v:unknown):number{if(typeof v==='number')return v;if(typeof v==='string')return parseFloat(v)||0;return 0;}
function round2(n:number){return Math.round(n*100)/100;}

const SP={fill:'none',stroke:'currentColor',strokeWidth:'2',strokeLinecap:'round' as const,strokeLinejoin:'round' as const};
const CartIcon=()=><svg width="17" height="17" viewBox="0 0 24 24" {...SP}><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>;
const SearchIcon=()=><svg width="17" height="17" viewBox="0 0 24 24" {...SP}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const UserIcon=()=><svg width="14" height="14" viewBox="0 0 24 24" {...SP}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const ZapIcon=()=><svg width="13" height="13" viewBox="0 0 24 24" {...SP}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
const CheckIcon=()=><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const XIcon=({s=13}:{s?:number})=><svg width={s} height={s} viewBox="0 0 24 24" {...SP}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;

const CSS=`
.pr{
  height:100%;
  display:flex;
  flex-direction:column;
  background:#f3f4f6;
  font-family:Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size:14px;
  color:#0f172a;
  letter-spacing:-0.01em;
}
.tb{display:flex;align-items:center;height:50px;background:#1E293B;flex-shrink:0;padding:0 14px;gap:2px;}
.ts{display:flex;flex-direction:column;padding:0 12px;border-right:1px solid rgba(255,255,255,.08);}
.ts:last-child{border-right:none;}
.pb{display:flex;overflow:hidden;flex:1;gap:8px;padding:8px;}
.pl{flex:1;display:flex;flex-direction:column;gap:6px;overflow:hidden;min-width:0;}
.pright{width:358px;flex-shrink:0;display:flex;flex-direction:column;background:#fff;border-radius:14px;border:1px solid #E2E8F0;overflow:hidden;box-shadow:0 2px 10px rgba(0,0,0,.06);}
.btm{height:33px;background:#1E293B;flex-shrink:0;display:flex;align-items:center;padding:0 14px;gap:14px;overflow:hidden;}
.ps{width:100%;height:44px;padding:0 42px;border-radius:11px;border:2px solid #E2E8F0;background:#fff;font-size:14px;color:#1E293B;outline:none;transition:border-color .15s,box-shadow .15s;}
.ps:focus{border-color:#008060;box-shadow:0 0 0 3px rgba(0,128,96,.10);}
.ps::placeholder{color:#94A3B8;}
.sw{position:relative;flex-shrink:0;}
.si{position:absolute;left:13px;top:50%;transform:translateY(-50%);color:#94A3B8;pointer-events:none;}
.sh{position:absolute;right:11px;top:50%;transform:translateY(-50%);font-size:10px;color:#94A3B8;background:#F8FAFC;border:1px solid #E2E8F0;border-radius:4px;padding:1px 5px;font-family:inherit;}
.sd{position:absolute;z-index:60;left:0;right:0;top:calc(100%+5px);background:#fff;border:1px solid #E2E8F0;border-radius:11px;box-shadow:0 8px 24px rgba(0,0,0,.12);overflow:hidden;max-height:280px;overflow-y:auto;}
.si2{display:flex;align-items:center;gap:11px;padding:10px 14px;cursor:pointer;border-bottom:1px solid #F8FAFC;transition:background .1s;}
.si2:hover{background:#F8FAFC;}
.si2.active{background:#E3F1EC;border-left:4px solid #008060;padding-left:10px;}
.si2.active:hover{background:#E3F1EC;}
.cs{display:flex;gap:5px;overflow-x:auto;flex-shrink:0;padding-bottom:2px;}
.cs::-webkit-scrollbar{height:3px;}
.cs::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:99px;}
.cc{padding:5px 12px;border-radius:99px;border:1.5px solid #E2E8F0;background:#fff;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap;color:#64748B;transition:all .15s;flex-shrink:0;}
.cc:hover{border-color:#008060;color:#008060;}
.cc.ac{background:#008060;border-color:#008060;color:#fff;}
.pg{
  flex:1;
  min-height:0;
  overflow-y:auto;
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(165px,1fr));
  grid-auto-rows:142px;
  align-content:start;
  gap:10px;
  padding:2px;
}
.pg::-webkit-scrollbar{width:4px;}
.pg::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:99px;}
.pc{
  height:142px;
  background:#fff;
  border-radius:14px;
  border:1.5px solid #E2E8F0;
  padding:12px;
  cursor:pointer;
  transition:all .15s;
  display:flex;
  flex-direction:column;
  justify-content:space-between;
  position:relative;
}
.pc:hover{border-color:#008060;box-shadow:0 4px 12px rgba(0,128,96,.12);transform:translateY(-1px);}
.ca{flex:1;display:flex;flex-direction:column;background:#fff;border-radius:12px;border:1px solid #E2E8F0;overflow:hidden;}
.ch{display:flex;align-items:center;justify-content:space-between;padding:10px 13px;border-bottom:1px solid #F1F5F9;flex-shrink:0;}
.cb{flex:1;overflow-y:auto;}
.cb::-webkit-scrollbar{width:3px;}
.cb::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:99px;}
.cr{display:flex;align-items:flex-start;gap:10px;padding:10px 13px;border-bottom:1px solid #F8FAFC;cursor:pointer;transition:background .1s;}
.cr:hover{background:#FAFAFA;}
.cr.sel{background:#F0FBF7;border-left:3px solid #008060;padding-left:10px;}
.qb{width:24px;height:24px;border-radius:6px;border:1.5px solid #E2E8F0;background:#F8FAFC;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#374151;transition:all .1s;line-height:1;}
.qb:hover{background:#008060;border-color:#008060;color:#fff;}
.rm{background:none;border:none;cursor:pointer;color:#CBD5E1;padding:2px;display:flex;align-items:center;line-height:1;transition:color .12s;}
.rm:hover{color:#DC2626;}
.qi{width:30px;text-align:center;font-size:12px;font-weight:700;border:none;background:transparent;outline:none;}
.rs{padding:11px 13px;border-bottom:1px solid #F1F5F9;}
.rs:last-child{border-bottom:none;}
.pyb{display:flex;align-items:center;justify-content:center;gap:4px;padding:7px 9px;border-radius:8px;border:1.5px solid #E2E8F0;background:#F8FAFC;font-size:12px;font-weight:600;cursor:pointer;color:#374151;transition:all .15s;flex:1;}
.pyb:hover{border-color:#008060;background:#F0FBF7;color:#008060;}
.pl2{display:flex;align-items:center;gap:6px;background:#F8FAFC;border-radius:8px;padding:6px 8px;margin-bottom:4px;border:1px solid #F1F5F9;}
.tr{display:flex;justify-content:space-between;font-size:12.5px;padding:2px 0;}
.cob{width:100%;padding:13px;border:none;border-radius:0;font-size:14px;font-weight:700;cursor:pointer;transition:all .15s;display:flex;align-items:center;justify-content:center;gap:6px;}
.cob.go{background:#008060;color:#fff;}
.cob.go:hover{background:#006B51;}
.cob.no{background:#F1F5F9;color:#94A3B8;cursor:default;}
.mb{
  position:fixed;
  inset:0;
  background:rgba(15,23,42,.55);
  backdrop-filter:blur(5px);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:100;
  padding:24px;
}
.mc{
  background:#fff;
  border-radius:18px;
  box-shadow:0 24px 70px rgba(0,0,0,.22);
  width:auto;
  max-width:calc(100vw - 48px);
  overflow:hidden;
}
.pi{width:100%;padding:9px 12px;border-radius:8px;border:1.5px solid #E2E8F0;font-size:14px;outline:none;transition:border-color .15s;box-sizing:border-box;}
.pi:focus{border-color:#008060;box-shadow:0 0 0 3px rgba(0,128,96,.10);}
.pl3{font-size:12px;font-weight:600;color:#475569;margin-bottom:4px;display:block;}
.pb2{background:#008060;color:#fff;border:none;border-radius:8px;padding:9px 16px;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 2px 8px rgba(0,128,96,.25);transition:all .15s;}
.pb2:hover{background:#006B51;}
.pb2:disabled{opacity:.5;cursor:default;}
.sb{background:#fff;color:#374151;border:1.5px solid #E2E8F0;border-radius:8px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s;}
.sb:hover{border-color:#CBD5E1;background:#F8FAFC;}
@keyframes su{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
.su{animation:su .18s ease both;}
@keyframes fl{0%,100%{background:transparent}50%{background:#D1FAE5}}
.fl{animation:fl .4s ease;}
`;

export function PosPage() {
  const qc=useQueryClient();
  const tenantId=useAuthStore(s=>s.user?.tenantId);
  const cashierName=useAuthStore(s=>s.user?.fullName??'Cashier');
  const {symbol}=useCurrency();
  const [searchTerm,setSearchTerm]=useState('');
  const [searchResults,setSearchResults]=useState<Product[]>([]);
  const [searchActiveIdx,setSearchActiveIdx]=useState(0);
  const [searchFocused,setSearchFocused]=useState(false);
  const [activeCat,setActiveCat]=useState('all');
  const [cart,setCart]=useState<CartLine[]>([]);
  const [payments,setPayments]=useState<PaymentInput[]>([]);
  const [selectedIdx,setSelectedIdx]=useState<number|null>(null);
  const [lastReceipt,setLastReceipt]=useState<any>(null);
  const [showReceipt,setShowReceipt]=useState(false);
  const [overrideIdx,setOverrideIdx]=useState<number|null>(null);
  const [editLineIdx,setEditLineIdx]=useState<number|null>(null);
  const [showDiscount,setShowDiscount]=useState(false);
  const [cartDiscount,setCartDiscount]=useState<{type:'pct'|'fixed';value:number}>({type:'pct',value:0});
  const [showReturns,setShowReturns]=useState(false);
  const [showEndShift,setShowEndShift]=useState(false);
  const [showShortcuts,setShowShortcuts]=useState(false);
  const [showCustSearch,setShowCustSearch]=useState(false);
  const [selectedCustomer,setSelectedCustomer]=useState<{id:string;name:string}|null>(null);
  const [pointsToRedeem,setPointsToRedeem]=useState(0);
  const [flashIdx,setFlashIdx]=useState<number|null>(null);
  const searchRef=useRef<HTMLInputElement>(null);

  const shiftQ=useQuery({queryKey:['pos','shift'],queryFn:posApi.getCurrentShift});
  const hasShift=!!shiftQ.data;
  // Load the tenant's real terminals instead of assuming a fixed code. Use the
  // open shift's terminal when one exists (so checkout posts to the same
  // terminal), otherwise the first active terminal, with a last-resort fallback.
  const terminalsQ=useQuery({queryKey:['pos','terminals'],queryFn:posApi.listTerminals});
  const terminalCode=(shiftQ.data?.terminal_code)
    ?? ((terminalsQ.data??[]).find((t:any)=>t.status==='active')?.code)
    ?? (terminalsQ.data??[])[0]?.code
    ?? 'POS-01';
  const catsQ=useQuery({queryKey:['pos','cats'],queryFn:inventoryApi.listCategories,enabled:hasShift,staleTime:5*60*1000});
  const cats:any[]=catsQ.data??[];
  const prodsQ=useQuery({queryKey:['pos','prods',activeCat],queryFn:()=>inventoryApi.listProducts({pageSize:100,status:'active',...(activeCat!=='all'?{categoryId:activeCat}:{})} as any),enabled:hasShift,staleTime:60*1000});
  const allProds:Product[]=useMemo(()=>(prodsQ.data?.data??[]).map((p:any)=>({
    ...p,
    available_qty: p.available_qty ?? p.stock_qty ?? 0,
  })),[prodsQ.data]);

  useEffect(()=>{
    const t=setTimeout(async()=>{
      if(searchTerm.trim().length<1){setSearchResults([]);return;}
      try{setSearchResults(await posApi.lookupProduct(searchTerm.trim()));}catch{setSearchResults([]);}
    },180);
    return()=>clearTimeout(t);
  },[searchTerm]);

  useEffect(()=>{
    setSearchActiveIdx(0);
  },[searchResults]);

  useEffect(()=>{
    const el=document.getElementById(`search-row-${searchActiveIdx}`);
    el?.scrollIntoView({block:'nearest'});
  },[searchActiveIdx]);

  const rawTotals=useMemo(()=>{
    let sub=0,disc=0,tax=0;
    for(const l of cart){const g=l.unitPrice*l.quantity,d=g*(l.discountPercent/100),n=g-d;sub+=g;disc+=d;tax+=n*l.taxRate/100;}
    const cd=cartDiscount.type==='pct'?(sub-disc)*cartDiscount.value/100:cartDiscount.value;
    return{subtotal:round2(sub),discount:round2(disc),cartDiscount:round2(cd),tax:round2(tax),grand:round2(sub-disc-cd+tax)};
  },[cart,cartDiscount]);

  const redemptionQ=useQuery({queryKey:['pos','red',selectedCustomer?.id,pointsToRedeem],queryFn:()=>posApi.previewRedemption(selectedCustomer!.id,rawTotals.grand,pointsToRedeem),enabled:!!selectedCustomer&&pointsToRedeem>0});
  const redDisc=redemptionQ.data?.ok&&pointsToRedeem>0?redemptionQ.data.requestedDollars:0;
  const totals=useMemo(()=>({...rawTotals,redemptionDiscount:round2(redDisc),grand:round2(rawTotals.grand-redDisc)}),[rawTotals,redDisc]);
  const paidTotal=useMemo(()=>round2(payments.reduce((s,p)=>s+p.amount,0)),[payments]);
  const balance=round2(totals.grand-paidTotal);
  const change=balance<0?round2(Math.abs(balance)):0;
  const canCheckout=hasShift&&cart.length>0&&paidTotal+0.005>=totals.grand;

  const openShiftMut=useMutation({mutationFn:({openingFloat}:{openingFloat:number})=>posApi.openShift(terminalCode,openingFloat),onSuccess:()=>{toast.success('Shift opened');qc.invalidateQueries({queryKey:['pos']});},onError:(e)=>toast.error(getErrorMessage(e))});
  const closeShiftMut=useMutation({mutationFn:({actualCash}:{actualCash:number})=>posApi.closeShift(actualCash),onSuccess:()=>{toast.success('Shift closed');qc.invalidateQueries({queryKey:['pos']});setShowEndShift(false);},onError:(e)=>toast.error(getErrorMessage(e))});
  const createSaleMut=useMutation({
    mutationFn:(p:any)=>posApi.createSale(p),
    onSuccess:(data)=>{
      toast.success(`Sale complete · ${data.receiptNumber}`);
      const enriched={...data,
        lines:cart.map(l=>({name:l.name,sku:l.sku,quantity:l.quantity,unitPrice:l.unitPrice,
          discountPercent:l.discountPercent,taxRate:l.taxRate,
          lineTotal:round2((l.unitPrice*l.quantity)*(1-(l.discountPercent||0)/100)*(1+(l.taxRate||0)/100))})),
        cashierName,terminalCode,
        customerName:selectedCustomer?.name??null,
        payments:payments.map(p=>({method:p.method,amount:p.amount})),
        currencySymbol:symbol,
      };
      setLastReceipt(enriched);setShowReceipt(true);
      posApi.pushCustomerDisplay(terminalCode,{mode:'thank_you',receiptNumber:data.receiptNumber,grandTotal:data.grandTotal,changeDue:data.changeDue,loyaltyEarned:data.loyalty?.earnedPoints??0,message:'Thank you!'}).catch(()=>{});
      setCart([]);setPayments([]);setSelectedCustomer(null);setPointsToRedeem(0);setCartDiscount({type:'pct',value:0});setSelectedIdx(null);setSearchTerm('');
      qc.invalidateQueries({queryKey:['pos','prods']});
      setTimeout(()=>searchRef.current?.focus(),100);
    },
    onError:(e)=>toast.error(getErrorMessage(e)),
  });

  const addToCart=useCallback((p:Product)=>{
    if(toNum(p.available_qty)<=0){toast.error('Out of stock');return;}
    setCart(prev=>{
      const i=prev.findIndex(l=>l.productId===p.id);
      const ni=i>=0?i:prev.length;
      setFlashIdx(ni);setTimeout(()=>setFlashIdx(null),450);
      if(i>=0)return prev.map((l,j)=>j===i?{...l,quantity:l.quantity+1}:l);
      return[...prev,{productId:p.id,sku:p.sku,name:p.name,unitPrice:toNum(p.base_price),originalPrice:toNum(p.base_price),quantity:1,discountPercent:0,taxRate:toNum(p.tax_rate)}];
    });
    setSearchTerm('');setSearchResults([]);setSearchActiveIdx(0);
    searchRef.current?.focus();
  },[]);

  const updateLine=(idx:number,patch:Partial<CartLine>)=>setCart(prev=>prev.map((l,i)=>i===idx?{...l,...patch}:l));
  const removeLine=(idx:number)=>{setCart(prev=>prev.filter((_,i)=>i!==idx));setSelectedIdx(null);};

  useEffect(()=>{
    const t=setTimeout(()=>{
      if(!hasShift)return;
      posApi.pushCustomerDisplay(terminalCode,{mode:cart.length===0?'idle':'cart',lines:cart.map(l=>({name:l.name,sku:l.sku,quantity:l.quantity,unitPrice:l.unitPrice,lineTotal:round2((l.unitPrice*l.quantity)*(1-l.discountPercent/100)*(1+l.taxRate/100))})),subtotal:totals.subtotal,discount:round2(totals.discount+(totals.redemptionDiscount??0)),tax:totals.tax,grandTotal:totals.grand}).catch(()=>{});
    },300);
    return()=>clearTimeout(t);
  },[cart,totals,hasShift,terminalCode]);

  const addPayment=(method:PaymentInput['method'])=>setPayments(p=>[...p,{method,amount:balance>0?balance:0}]);
  const removePayment=(i:number)=>setPayments(p=>p.filter((_,j)=>j!==i));
  const updatePayment=(i:number,patch:Partial<PaymentInput>)=>setPayments(p=>p.map((pp,j)=>j===i?{...pp,...patch}:pp));
  const checkout=()=>{if(!canCheckout)return;createSaleMut.mutate({terminalCode,customerId:selectedCustomer?.id??null,lines:cart.map(l=>({productId:l.productId,quantity:l.quantity,unitPrice:l.unitPrice,discountPercent:l.discountPercent})),payments,loyaltyPointsUsed:redDisc>0?pointsToRedeem:0});};

  useEffect(() => {
  const h = (e: globalThis.KeyboardEvent) => {
    const inp =
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      e.target instanceof HTMLSelectElement;

    // Global shortcuts must work even when input is focused
    if (e.key === 'F4') {
      e.preventDefault();
      setShowCustSearch(true);
      return;
    }

    if (e.key === 'F2') {
      e.preventDefault();
      setCart([]);
      setPayments([]);
      setSelectedIdx(null);
      searchRef.current?.focus();
      return;
    }

    if (e.key === 'F6') {
      e.preventDefault();
      setShowDiscount(true);
      return;
    }

    if (e.key === 'F8') {
      e.preventDefault();
      document.getElementById('hbtn')?.click();
      return;
    }

    if (e.key === 'F9') {
      e.preventDefault();
      document.getElementById('rbtn')?.click();
      return;
    }

    if (e.key === 'F10' || e.key === 'F12') {
      e.preventDefault();
      if (canCheckout) checkout();
      return;
    }

    if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'b' || e.key.toLowerCase() === 'f')) {
      e.preventDefault();
      searchRef.current?.focus();
      return;
    }

    // Do not run cart shortcuts while typing
    if (inp) {
      if (e.key === 'Escape') (e.target as HTMLElement).blur();
      return;
    }

    if (e.key === '?' || e.key === '/') {
      e.preventDefault();
      setShowShortcuts(v => !v);
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      setShowReturns(true);
      return;
    }

    if (e.key === 'ArrowDown' && cart.length > 0) {
      e.preventDefault();
      setSelectedIdx(i => (i === null ? 0 : Math.min(cart.length - 1, i + 1)));
      return;
    }

    if (e.key === 'ArrowUp' && cart.length > 0) {
      e.preventDefault();
      setSelectedIdx(i => (i === null ? cart.length - 1 : Math.max(0, i - 1)));
      return;
    }

    if (e.key === 'Delete' && selectedIdx !== null) {
      e.preventDefault();
      removeLine(selectedIdx);
      return;
    }

    if (e.key === 'Enter' && selectedIdx !== null) {
      e.preventDefault();
      setEditLineIdx(selectedIdx);
      return;
    }

    if ((e.key === '+' || e.key === '=') && selectedIdx !== null) {
      e.preventDefault();
      updateLine(selectedIdx, { quantity: cart[selectedIdx].quantity + 1 });
      return;
    }

    if (e.key === '-' && selectedIdx !== null) {
      e.preventDefault();
      updateLine(selectedIdx, { quantity: Math.max(1, cart[selectedIdx].quantity - 1) });
      return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p' && selectedIdx !== null) {
      e.preventDefault();
      setOverrideIdx(selectedIdx);
      return;
    }
  };

  window.addEventListener('keydown', h);
  return () => window.removeEventListener('keydown', h);
}, [cart, selectedIdx, canCheckout]);

  if(shiftQ.isLoading)return<div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{color:'#64748B',fontSize:14}}>Loading POS…</div></div>;
  if(!hasShift)return<OpenShiftScreen terminalCode={terminalCode} cashierName={cashierName} onOpen={f=>openShiftMut.mutate({openingFloat:f})} loading={openShiftMut.isPending}/>;

  const gridProds=activeCat==='all'?allProds:allProds.filter((p:any)=>p.category_id===activeCat);

  return(<>
    <style>{CSS}</style>
    <div className="pr">
      {/* TOP BAR */}
      <div className="tb">
        <div style={{display:'flex',alignItems:'center',gap:8,paddingRight:12,borderRight:'1px solid rgba(255,255,255,.08)'}}>
          <div style={{width:27,height:27,borderRadius:7,background:'#008060',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:900,fontSize:12,color:'#fff'}}>O</div>
          <div><div style={{fontSize:11,fontWeight:700,color:'#fff',lineHeight:1}}>OmniRetail</div><div style={{fontSize:9.5,color:'#64748B',fontFamily:'inherit'}}>{terminalCode}</div></div>
        </div>
        {[{l:'CASHIER',v:cashierName,m:false},{l:'SHIFT',v:fmt(shiftQ.data.shift_total),m:true},{l:'SALES',v:String(shiftQ.data.sale_count),m:true}].map(s=>(
          <div key={s.l} className="ts">
            <div style={{fontSize:9,color:'#475569',fontWeight:700,letterSpacing:'.06em'}}>{s.l}</div>
            <div style={{fontSize:12.5,fontWeight:800,color:'#F1F5F9',fontFamily:s.m?'inherit':'inherit'}}>{s.v}</div>
          </div>
        ))}
        <div style={{flex:1}}/>
        <div style={{display:'flex',alignItems:'center',gap:5,padding:'4px 9px',background:'rgba(16,185,129,.15)',borderRadius:99,border:'1px solid rgba(16,185,129,.3)',marginRight:8}}>
          <div style={{width:5,height:5,borderRadius:'50%',background:'#10B981'}}/><span style={{fontSize:10,fontWeight:700,color:'#10B981'}}>ONLINE</span>
        </div>
        {tenantId&&<button style={{fontSize:11,fontWeight:600,color:'#94A3B8',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.10)',borderRadius:7,padding:'4px 8px',cursor:'pointer'}} onClick={()=>window.open(`/pos/customer-display/${tenantId}/${terminalCode}`,'cd','width=1024,height=768')}>📺</button>}
        <HoldCartButton cart={cart} terminalCode={terminalCode} onHeld={()=>{setCart([]);setPayments([]);setSelectedIdx(null);}}/>
        <ResumeCartMenu terminalCode={terminalCode} onResumed={d=>{if(Array.isArray(d?.lines))setCart(d.lines);if(Array.isArray(d?.payments))setPayments(d.payments);toast.success('Cart resumed');}}/>
        <button style={{fontSize:11,fontWeight:600,color:'#94A3B8',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.10)',borderRadius:7,padding:'4px 8px',cursor:'pointer',marginLeft:4}} onClick={()=>setShowReturns(true)}>↩ Return</button>
        <button style={{fontSize:11,fontWeight:600,color:'#F87171',background:'rgba(239,68,68,.10)',border:'1px solid rgba(239,68,68,.2)',borderRadius:7,padding:'4px 8px',cursor:cart.length>0?'not-allowed':'pointer',marginLeft:4,opacity: cart.length > 0 ? 0.5 : 1}} onClick={()=>!cart.length&&setShowEndShift(true)}>⏹ End shift</button>
      </div>

      {/* BODY */}
      <div className="pb">
        {/* LEFT: search + categories + product grid */}
        <div className="pl">
          {/* Search */}
          <div className="sw">
            <div className="si"><SearchIcon/></div>
            <input ref={searchRef} autoFocus className="ps" placeholder="Search by name, SKU, or scan barcode…" value={searchTerm}
              onChange={e=>setSearchTerm(e.target.value)}
              onFocus={()=>setSearchFocused(true)}
              onBlur={()=>setTimeout(()=>setSearchFocused(false),150)}
              onKeyDown={e=>{
                if(searchResults.length>0){
                  if(e.key==='ArrowDown'){
                    e.preventDefault();
                    setSearchActiveIdx(i=>Math.min(i+1,searchResults.length-1));
                    return;
                  }
                  if(e.key==='ArrowUp'){
                    e.preventDefault();
                    setSearchActiveIdx(i=>Math.max(i-1,0));
                    return;
                  }
                  if(e.key==='Enter'){
                    e.preventDefault();
                    addToCart(searchResults[searchActiveIdx]??searchResults[0]);
                    return;
                  }
                }
                if(e.key==='Escape'){
                  e.preventDefault();
                  setSearchTerm('');
                  setSearchResults([]);
                  setSearchActiveIdx(0);
                }
              }}/>
            <div className="sh">Ctrl+B</div>
            {searchResults.length>0&&searchFocused&&(
              <div className="sd su">
                {searchResults.map((p,idx)=>{const qty=toNum(p.available_qty),low=qty>0&&qty<=10,out=qty<=0;return(
                  <div key={p.id} id={`search-row-${idx}`} className={`si2 ${idx===searchActiveIdx?'active':''}`} onMouseEnter={()=>setSearchActiveIdx(idx)} onMouseDown={()=>addToCart(p)}>
                    <div style={{width:38,height:38,borderRadius:8,background:out?'#FEF2F2':low?'#FFFBEB':'#F0FBF7',color:out?'#DC2626':low?'#D97706':'#008060',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:13,flexShrink:0}}>{p.name.trim()[0]?.toUpperCase()||'?'}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,color:'#1E293B',fontSize:13.5}}>{p.name}</div>
                      <div style={{fontSize:11,color:'#94A3B8',fontFamily:'inherit'}}>{p.sku}<span style={{marginLeft:8,color:out?'#DC2626':low?'#D97706':'#64748B',fontWeight:600}}>{out?'✗ Out of stock':low?`⚠ ${qty} left`:`${qty} in stock`}</span></div>
                    </div>
                    <div style={{fontWeight:800,fontSize:15,color:'#1E293B',fontFamily:'inherit'}}>{fmt(p.base_price)}</div>
                  </div>
                );})}
              </div>
            )}
          </div>

          {/* Category chips */}
          <div className="cs">
            <button className={`cc${activeCat==='all'?' ac':''}`} onClick={()=>setActiveCat('all')}>All products</button>
            {cats.map((c:any)=><button key={c.id} className={`cc${activeCat===c.id?' ac':''}`} onClick={()=>setActiveCat(c.id)}>{c.name}</button>)}
          </div>

          {/* Product grid */}
          <div className="pg">
            {gridProds.length===0&&!prodsQ.isLoading?(
              <div style={{gridColumn:'1/-1',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:32,textAlign:'center',color:'#94A3B8'}}>
                <div style={{fontSize:24,marginBottom:8}}>📦</div>
                <div style={{fontSize:13,fontWeight:600}}>No products here</div>
                <div style={{fontSize:12,marginTop:4}}>Try a different category or search above</div>
              </div>
            ):gridProds.map(p=>{
              const qty=toNum(p.available_qty),low=qty>0&&qty<=10,out=qty<=0;
              const inCart=cart.find(l=>l.productId===p.id);
              return(
                <div key={p.id} className="pc" style={{opacity: out ? 0.4 : 1,cursor:out?'not-allowed':'pointer'}} onClick={()=>!out&&addToCart(p)}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                    <div style={{width:30,height:30,borderRadius:7,background:out?'#FEF2F2':low?'#FFFBEB':'#F0FBF7',color:out?'#DC2626':low?'#D97706':'#008060',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:12,flexShrink:0}}>{p.name.trim()[0]?.toUpperCase()||'?'}</div>
                    {inCart&&<div style={{fontSize:10,fontWeight:700,color:'#fff',background:'#008060',borderRadius:99,padding:'1px 6px'}}>{inCart.quantity}</div>}
                  </div>
                  <div style={{fontWeight:600,color:'#1E293B',fontSize:11.5,lineHeight:1.3,flex:1}}>{p.name}</div>
                  <div>
                    <div style={{fontWeight:800,color:'#008060',fontSize:13,fontFamily:'inherit'}}>{fmt(p.base_price)}</div>
                    <div style={{fontSize:10,color:out?'#DC2626':low?'#D97706':'#94A3B8',fontWeight:600}}>{out?'Out of stock':low?`${qty} left`:`${qty} in stock`}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="pright">
          <div style={{flex:1,overflowY:'auto'}}>
            {/* Customer */}
            <div className="rs">
              <CustomerPicker selected={selectedCustomer} onSelect={c=>{setSelectedCustomer(c);setPointsToRedeem(0);}} forceOpen={showCustSearch} onClose={()=>setShowCustSearch(false)}/>
              {selectedCustomer&&<div style={{marginTop:8}}><LoyaltyPanel customerId={selectedCustomer.id} grandTotalBeforeRedemption={rawTotals.grand} pointsToRedeem={pointsToRedeem} onChangePoints={setPointsToRedeem}/></div>}
            </div>

            {/* Cart */}
            <div className="rs" style={{padding:0,overflow:'hidden'}}>
              <div className="ch">
                <div style={{display:'flex',alignItems:'center',gap:7}}>
                  <CartIcon/><span style={{fontWeight:700,fontSize:13,color:'#1E293B'}}>Order{cart.length>0&&<span style={{color:'#94A3B8',fontWeight:400,fontSize:11.5}}> · {cart.length} item{cart.length>1?'s':''}</span>}</span>
                </div>
                {cart.length>0&&(
                  <div style={{display:'flex',gap:4}}>
                    <button onClick={()=>setShowDiscount(true)} style={{fontSize:11,fontWeight:600,color:'#64748B',background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:6,padding:'3px 7px',cursor:'pointer'}}>% Disc</button>
                    <button onClick={()=>{setCart([]);setPayments([]);setSelectedIdx(null);}} style={{fontSize:11,fontWeight:600,color:'#DC2626',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:6,padding:'3px 7px',cursor:'pointer'}}>Clear</button>
                  </div>
                )}
              </div>
              <div className="cb" style={{maxHeight:260}}>
                {cart.length===0?(
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'20px 14px',textAlign:'center'}}>
                    <div style={{color:'#CBD5E1',marginBottom:6}}><CartIcon/></div>
                    <div style={{fontSize:12,color:'#94A3B8'}}>Tap a product or scan a barcode</div>
                  </div>
                ):cart.map((l,i)=>{
                  const g=l.unitPrice*l.quantity,d=g*l.discountPercent/100,n=g-d;
                  const lt=round2(n*(1+l.taxRate/100));
                  const ov=l.unitPrice!==l.originalPrice,isSel=selectedIdx===i,isF=flashIdx===i;
                  return(
                    <div key={i} className={`cr${isSel?' sel':''}${isF?' fl':''}`} onClick={()=>setSelectedIdx(i)}>
                      <div style={{width:30,height:30,borderRadius:7,background:isSel?'#D1FAE5':'#F1F5F9',color:isSel?'#008060':'#64748B',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:11,flexShrink:0,transition:'all .15s'}}>{l.name.trim()[0]?.toUpperCase()||'?'}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,color:'#1E293B',fontSize:12.5,marginBottom:1}}>{l.name}</div>
                        <div style={{fontSize:10,color:'#94A3B8',fontFamily:'inherit',marginBottom:5}}>{l.sku}</div>
                        <div style={{display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
                          <div style={{display:'flex',alignItems:'center',gap:3}}>
                            <button className="qb" onClick={e=>{e.stopPropagation();updateLine(i,{quantity:Math.max(1,l.quantity-1)});}}>−</button>
                            <input className="qi" type="number" min="1" value={l.quantity} onClick={e=>e.stopPropagation()} onChange={e=>updateLine(i,{quantity:Math.max(1,parseInt(e.target.value)||1)})}/>
                            <button className="qb" onClick={e=>{e.stopPropagation();updateLine(i,{quantity:l.quantity+1});}}>+</button>
                          </div>
                          <button onClick={e=>{e.stopPropagation();setOverrideIdx(i);}} style={{fontSize:11,fontWeight:700,color:ov?'#D97706':'#475569',background:ov?'#FFFBEB':'#F8FAFC',border:`1px solid ${ov?'#FDE68A':'#E2E8F0'}`,borderRadius:5,padding:'2px 5px',cursor:'pointer'}}>
                            {ov&&<span style={{textDecoration:'line-through',color:'#94A3B8',marginRight:2,fontWeight:400}}>{fmt(l.originalPrice)}</span>}{fmt(l.unitPrice)}
                          </button>
                          {l.discountPercent>0&&<span style={{fontSize:10,color:'#059669',fontWeight:600,background:'#D1FAE5',borderRadius:4,padding:'1px 4px'}}>-{l.discountPercent}%</span>}
                        </div>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:3,flexShrink:0}}>
                        <div style={{fontWeight:800,fontSize:13.5,color:'#1E293B',fontFamily:'inherit'}}>{fmt(lt)}</div>
                        {l.discountPercent>0&&<div style={{fontSize:10,color:'#94A3B8',textDecoration:'line-through',fontFamily:'inherit'}}>{fmt(g)}</div>}
                        <button className="rm" title="Remove item (Del)" onClick={e=>{e.stopPropagation();removeLine(i);}}><XIcon s={13}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {cart.length>0&&selectedIdx!==null&&(
                <div style={{display:'flex',gap:7,padding:'4px 12px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',flexShrink:0}}>
                  {[['↑↓','Nav'],['Enter','Edit'],['+ −','Qty'],['Del','Rm'],['Ctrl+P','Price']].map(([k,l])=>(
                    <div key={k} style={{fontSize:9.5,color:'#94A3B8'}}><span style={{fontFamily:'inherit',fontWeight:700,color:'#64748B'}}>{k}</span><span style={{marginLeft:2}}>{l}</span></div>
                  ))}
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="rs">
              <div style={{background:'#F8FAFC',borderRadius:9,padding:'9px 11px',border:'1px solid #F1F5F9'}}>
                {[['Subtotal',fmt(totals.subtotal),false],...(totals.discount>0?[['Item disc',`−${fmt(totals.discount)}`,true]]:[] as any),...(totals.cartDiscount>0?[['Cart disc',`−${fmt(totals.cartDiscount)}`,true]]:[] as any),['Tax',fmt(totals.tax),false],...(totals.redemptionDiscount>0?[['★ Points',`−${fmt(totals.redemptionDiscount)}`,true]]:[] as any)].map(([label,value,m]:any)=>(
                  <div key={label} className="tr" style={{color:m?'#059669':'#64748B'}}><span>{label}</span><span style={{fontFamily:'inherit',fontWeight:600}}>{value}</span></div>
                ))}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',paddingTop:8,marginTop:7,borderTop:'2px solid #E2E8F0'}}>
                  <span style={{fontWeight:700,fontSize:13,color:'#1E293B'}}>Total</span>
                  <span style={{fontWeight:900,fontSize:21,color:'#1E293B',fontFamily:'inherit',letterSpacing:'-.03em'}}>{fmt(totals.grand)}</span>
                </div>
              </div>
            </div>

            {/* Payment */}
            <div className="rs">
              <div style={{fontSize:10,fontWeight:700,color:'#94A3B8',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:7}}>Payment</div>
              <div style={{display:'flex',gap:4,marginBottom:8}}>
                {([{k:'cash',l:'💵 Cash'},{k:'card',l:'💳 Card'},{k:'wallet',l:'📱 Wallet'},{k:'gift_card',l:'🎁 Gift'}] as any[]).map(m=><button key={m.k} className="pyb" onClick={()=>addPayment(m.k)}>{m.l}</button>)}
              </div>
              {payments.some(p=>p.method==='cash')&&(
                <div style={{display:'flex',gap:4,marginBottom:7,flexWrap:'wrap'}}>
                  {[{l:'Exact',v:totals.grand},{l:'500',v:500},{l:'1K',v:1000},{l:'2K',v:2000},{l:'5K',v:5000},{l:'10K',v:10000}].map(q=>(
                    <button key={q.l} onClick={()=>{const ci=payments.findIndex(p=>p.method==='cash');if(ci>=0)updatePayment(ci,{amount:q.v});}} style={{fontSize:10.5,fontWeight:700,padding:'3px 7px',borderRadius:6,border:'1px solid #E2E8F0',background:'#F8FAFC',cursor:'pointer',color:'#475569',transition:'all .1s'}} onMouseEnter={e=>{e.currentTarget.style.background='#F0FBF7';e.currentTarget.style.borderColor='#BBF7D0';e.currentTarget.style.color='#008060';}} onMouseLeave={e=>{e.currentTarget.style.background='#F8FAFC';e.currentTarget.style.borderColor='#E2E8F0';e.currentTarget.style.color='#475569';}}>{q.l}</button>
                  ))}
                </div>
              )}
              {payments.length===0?<div style={{textAlign:'center',padding:10,fontSize:12,color:'#94A3B8',background:'#F8FAFC',borderRadius:8,border:'1.5px dashed #E2E8F0'}}>Select a method above</div>
               :payments.map((p,i)=>(
                <div key={i} className="pl2">
                  <div style={{fontSize:10,fontWeight:700,color:'#475569',background:'#E2E8F0',borderRadius:5,padding:'2px 5px',textTransform:'uppercase',flexShrink:0,minWidth:40,textAlign:'center'}}>{p.method==='gift_card'?'Gift':p.method==='bank_transfer'?'Bank':p.method}</div>
                  <div style={{position:'relative',flex:1}}>
                    <span style={{position:'absolute',left:7,top:'50%',transform:'translateY(-50%)',fontSize:12,color:'#94A3B8'}}>{symbol}</span>
                    <input type="number" step=".01" min="0" value={p.amount} onChange={e=>updatePayment(i,{amount:parseFloat(e.target.value)||0})} style={{width:'100%',padding:'5px 8px 5px 20px',borderRadius:7,border:'1.5px solid #E2E8F0',fontSize:13,fontFamily:'inherit',fontWeight:700,textAlign:'right',outline:'none',background:'#fff'}}/>
                  </div>
                  <button onClick={()=>removePayment(i)} style={{width:22,height:22,display:'flex',alignItems:'center',justifyContent:'center',border:'none',background:'none',color:'#94A3B8',cursor:'pointer',borderRadius:5}} onMouseEnter={e=>{e.currentTarget.style.background='#FEF2F2';e.currentTarget.style.color='#DC2626';}} onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color='#94A3B8';}}><XIcon/></button>
                </div>
              ))}
              {payments.length>0&&(
                <div style={{marginTop:7,padding:'7px 9px',background:balance>0.01?'#FEF2F2':change>0?'#F0FBF7':'#F8FAFC',borderRadius:8,border:`1px solid ${balance>0.01?'#FECACA':change>0?'#BBF7D0':'#F1F5F9'}`}}>
                  <div className="tr" style={{color:'#64748B'}}><span>Paid</span><span style={{fontFamily:'inherit',fontWeight:700}}>{fmt(paidTotal)}</span></div>
                  <div className="tr" style={{color:balance>0.01?'#DC2626':change>0?'#059669':'#64748B',fontWeight:700}}>
                    <span>{balance>0.01?'Balance due':change>0?'💵 Change':' Balanced'}</span>
                    <span style={{fontFamily:'inherit',fontSize:14}}>{fmt(balance>0.01?balance:change)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <button className={`cob ${canCheckout?'go':'no'}`} onClick={checkout} disabled={!canCheckout||createSaleMut.isPending}>
            {createSaleMut.isPending?'Processing…':cart.length===0?'Add items to begin':!canCheckout?`${fmt(totals.grand)} — add payment`:<><ZapIcon/> Charge {fmt(totals.grand)} · F10</>}
          </button>
        </div>
      </div>

      {/* BOTTOM BAR */}
      <div className="btm">
        {[['Ctrl+B','Barcode'],['F2','New'],['F4','Customer'],['F6','Discount'],['F8','Hold'],['F9','Recall'],['F10','Checkout'],['Ctrl+⇧+Z','Return'],['?','Help']].map(([k,l])=>(
          <div key={k} style={{display:'flex',alignItems:'center',gap:3,fontSize:10,color:'#475569',flexShrink:0}}>
            <span style={{fontFamily:'inherit',fontWeight:700,color:'#94A3B8',background:'rgba(255,255,255,.07)',border:'1px solid rgba(255,255,255,.10)',borderRadius:3,padding:'0 3px',fontSize:9.5}}>{k}</span>
            <span>{l}</span>
          </div>
        ))}
        <div style={{flex:1}}/>
        <div style={{fontSize:10,color:'#475569',fontFamily:'inherit'}}>{new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</div>
      </div>
    </div>

    {showReceipt&&lastReceipt&&<ReceiptModal receipt={lastReceipt} onClose={()=>setShowReceipt(false)}/>}
    {overrideIdx!==null&&cart[overrideIdx]&&<PriceOverrideModal terminalCode={terminalCode} productId={cart[overrideIdx].productId} currentPrice={cart[overrideIdx].unitPrice} onClose={()=>setOverrideIdx(null)} onApproved={p=>{updateLine(overrideIdx,{unitPrice:p});setOverrideIdx(null);}}/>}
    {editLineIdx!==null&&cart[editLineIdx]&&<EditLineModal line={cart[editLineIdx]} onClose={()=>setEditLineIdx(null)} onUpdate={p=>{updateLine(editLineIdx,p);setEditLineIdx(null);}} onRemove={()=>{removeLine(editLineIdx);setEditLineIdx(null);}}/>}
    {showDiscount&&<CartDiscountModal current={cartDiscount} onApply={d=>{setCartDiscount(d);setShowDiscount(false);}} onClose={()=>setShowDiscount(false)}/>}
    {showReturns&&<ReturnModal onClose={()=>setShowReturns(false)}/>}
    {showEndShift&&shiftQ.data&&<EndShiftModal shiftId={shiftQ.data.id} onClose={()=>setShowEndShift(false)} onConfirm={cash=>closeShiftMut.mutate({actualCash:cash})} loading={closeShiftMut.isPending}/>}
    {showShortcuts&&<ShortcutsModal onClose={()=>setShowShortcuts(false)}/>}
  </>);
}

function OpenShiftScreen({terminalCode,cashierName,onOpen,loading}:{terminalCode:string;cashierName:string;onOpen:(f:number)=>void;loading:boolean}) {
  const [f,setF]=useState(100);
  return(<div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'linear-gradient(145deg,#F0FBF7,#E8F5E9)'}}>
    <div style={{maxWidth:400,width:'100%',margin:'0 24px'}}>
      <div style={{textAlign:'center',marginBottom:24}}>
        <div style={{width:56,height:56,borderRadius:16,background:'#008060',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:900,fontSize:22,margin:'0 auto 12px',boxShadow:'0 8px 24px rgba(0,128,96,.30)'}}>O</div>
        <h1 style={{fontSize:24,fontWeight:900,color:'#111827',letterSpacing:'-.02em',margin:'0 0 6px'}}>Open your shift</h1>
        <p style={{fontSize:13,color:'#6B7280'}}>Terminal <span style={{fontFamily:'inherit',fontWeight:700,color:'#374151'}}>{terminalCode}</span> · {cashierName}</p>
      </div>
      <div style={{background:'#fff',borderRadius:16,padding:24,boxShadow:'0 8px 32px rgba(0,0,0,0.08)',border:'1px solid #E5E7EB'}}>
        <label style={{fontSize:13,fontWeight:700,color:'#374151',display:'block',marginBottom:7}}>Opening float (cash in drawer)</label>
        <div style={{position:'relative',marginBottom:6}}>
          <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:16,color:'#9CA3AF',fontWeight:600}}>$</span>
          <input type="number" step=".01" min="0" value={f} onChange={e=>setF(parseFloat(e.target.value)||0)} autoFocus onKeyDown={e=>{if(e.key==='Enter')onOpen(f);}}
            style={{width:'100%',padding:'12px 12px 12px 32px',borderRadius:10,border:'2px solid #E5E7EB',fontSize:20,fontFamily:'inherit',fontWeight:800,outline:'none',boxSizing:'border-box'}} onFocus={e=>e.target.style.borderColor='#008060'} onBlur={e=>e.target.style.borderColor='#E5E7EB'}/>
        </div>
        <div style={{fontSize:11.5,color:'#9CA3AF',marginBottom:16}}>Count the cash before opening.</div>
        <button onClick={()=>onOpen(f)} disabled={loading} style={{width:'100%',padding:'12px',background:'#008060',color:'#fff',border:'none',borderRadius:10,fontSize:14.5,fontWeight:700,cursor:loading?'default':'pointer',boxShadow:'0 4px 16px rgba(0,128,96,.30)',opacity:loading?.7:1}}>
          {loading?'Opening…':'⚡ Open shift & start selling'}
        </button>
        <div style={{display:'flex',gap:5,marginTop:9}}>
          {[0,100,500,1000,5000].map(v=><button key={v} onClick={()=>setF(v)} style={{flex:1,padding:'6px 0',fontSize:11,fontWeight:700,borderRadius:7,border:'1.5px solid #E5E7EB',background:f===v?'#F0FBF7':'#F9FAFB',color:f===v?'#008060':'#6B7280',cursor:'pointer',transition:'all .1s'}}>{v===0?'None':v>=1000?`${v/1000}K`:v}</button>)}
        </div>
      </div>
    </div>
  </div>);
}

function ReturnModal({onClose}:{onClose:()=>void}) {
  const [step,setStep]=useState<'search'|'items'>('search');
  const [receiptNo,setReceiptNo]=useState('');
  const [sale,setSale]=useState<any>(null);
  const [selected,setSelected]=useState<Record<string,number>>({});
  const [reason,setReason]=useState('Customer changed mind');
  const [refundMethod,setRefundMethod]=useState<'cash'|'store_credit'|'original'>('original');
  const [searching,setSearching]=useState(false);
  const searchSale=async()=>{
    if(!receiptNo.trim())return;setSearching(true);
    try{const data=await posApi.listSales({limit:200}) as any[];const found=data.find((s:any)=>s.receipt_number===receiptNo.trim()||s.receiptNumber===receiptNo.trim());if(!found){toast.error('Receipt not found');return;}const detail=await posApi.getSale(found.id);setSale(detail);setStep('items');}
    catch(e){toast.error(getErrorMessage(e));}finally{setSearching(false);}
  };
  const returnMut=useMutation({
    mutationFn:()=>posApi.processReturn({originalSaleId:sale.id,lines:Object.entries(selected).filter(([,qty])=>(qty as number)>0).map(([pid,qty])=>{const sl=sale.lines.find((l:any)=>l.product_id===pid);return{originalSaleLineId:sl?.id??pid,quantity:qty as number,condition:'resellable' as const};}),refundMethod,reason}),
    onSuccess:(d)=>{toast.success(`Refund processed · ${fmt(d.refundAmount)}`);onClose();},
    onError:(e)=>toast.error(getErrorMessage(e)),
  });
  const refTotal=sale?.lines?.reduce((s:number,l:any)=>s+round2(l.unit_price*(selected[l.product_id]??0)*(1-l.discount_percent/100)*(1+l.tax_rate/100)),0)??0;
  return(<div className="mb" onClick={onClose}>
    <div
  className="mc su"
  onClick={e => e.stopPropagation()}
  style={{
    maxWidth: 430,
    borderRadius: 20,
  }}
>
      <div style={{padding:'18px 20px 10px',borderBottom:'1px solid #F1F5F9'}}>
        <div style={{fontSize:10,fontWeight:700,color:'#008060',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>Return · Ctrl+Shift+Z</div>
        <div style={{fontWeight:800,fontSize:16,color:'#1E293B'}}>{step==='search'?'Find receipt':sale?.receipt_number||sale?.receiptNumber}</div>
      </div>
      {step==='search'&&<div style={{padding:'14px 20px 18px'}}>
        <label className="pl3">Receipt number</label>
        <div style={{display:'flex',gap:7}}>
          <input className="pi" style={{flex:1}} value={receiptNo} autoFocus placeholder="e.g. RCP-00001" onChange={e=>setReceiptNo(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')searchSale();}}/>
          <button className="pb2" onClick={searchSale} disabled={searching}>{searching?'…':'Find'}</button>
        </div>
      </div>}
      {step==='items'&&sale&&<div style={{padding:'10px 20px'}}>
        <div style={{maxHeight:260,overflowY:'auto',border:'1px solid #F1F5F9',borderRadius:9,marginBottom:12}}>
          {(sale.lines||[]).map((l:any)=>{const maxQ=l.quantity-(l.returned_qty||0),sq=selected[l.product_id]??0;return(
            <div key={l.product_id} style={{display:'flex',alignItems:'center',gap:9,padding:'9px 11px',borderBottom:'1px solid #F8FAFC'}}>
              <input type="checkbox" checked={sq>0} onChange={e=>setSelected(p=>({...p,[l.product_id]:e.target.checked?1:0}))} style={{width:14,height:14,accentColor:'#008060'}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:'#1E293B'}}>{l.product_name}</div>
                <div style={{fontSize:11,color:'#94A3B8'}}>Qty: {l.quantity} · {fmt(l.unit_price)}{l.returned_qty>0?` · Returned: ${l.returned_qty}`:''}</div>
              </div>
              {sq>0&&<div style={{display:'flex',alignItems:'center',gap:3}}>
                <button className="qb" onClick={()=>setSelected(p=>({...p,[l.product_id]:Math.max(0,sq-1)}))} style={{width:20,height:20}}>−</button>
                <span style={{width:18,textAlign:'center',fontSize:12,fontWeight:700}}>{sq}</span>
                <button className="qb" onClick={()=>setSelected(p=>({...p,[l.product_id]:Math.min(maxQ,sq+1)}))} style={{width:20,height:20}}>+</button>
              </div>}
              <div style={{fontSize:12,fontFamily:'inherit',color:'#1E293B',fontWeight:600,minWidth:60,textAlign:'right'}}>{fmt(l.unit_price*l.quantity)}</div>
            </div>
          );})}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
          <div><label className="pl3">Reason</label><select className="pi" value={reason} onChange={e=>setReason(e.target.value)}>{['Customer changed mind','Wrong item','Damaged','Expired','Pricing issue','Other'].map(r=><option key={r}>{r}</option>)}</select></div>
          <div><label className="pl3">Refund method</label><select className="pi" value={refundMethod} onChange={e=>setRefundMethod(e.target.value as any)}><option value="original">Original method</option><option value="cash">Cash</option><option value="store_credit">Store credit</option></select></div>
        </div>
        {Object.values(selected).some(v=>v>0)&&<div style={{background:'#F0FBF7',border:'1px solid #BBF7D0',borderRadius:8,padding:'8px 12px',fontSize:13,color:'#059669',fontWeight:700}}>Refund: {fmt(refTotal)}</div>}
      </div>}
      <div style={{padding:'10px 20px 16px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',display:'flex',gap:7,justifyContent:'flex-end'}}>
        <button className="sb" onClick={onClose}>Cancel</button>
        {step==='items'&&<><button className="sb" onClick={()=>setStep('search')}>← Back</button><button className="pb2" disabled={!Object.values(selected).some(v=>v>0)||returnMut.isPending} onClick={()=>returnMut.mutate()}>{returnMut.isPending?'Processing…':`Refund ${fmt(refTotal)}`}</button></>}
      </div>
    </div>
  </div>);
}

function EndShiftModal({shiftId,onClose,onConfirm,loading}:{shiftId:string;onClose:()=>void;onConfirm:(c:number)=>void;loading:boolean}) {
  const [declared,setDeclared]=useState('');
  const sumQ=useQuery({queryKey:['pos','sum',shiftId],queryFn:()=>posApi.shiftSummary(shiftId)});
  const d=sumQ.data;
  const expCash=round2((d?.opening_float??0)+(d?.cash_sales??0)-(d?.cash_refunds??0));
  const decNum=parseFloat(declared)||0;
  const variance=round2(decNum-expCash);
  return(<div className="mb" onClick={onClose}>
    <div className="mc su" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
      <div style={{padding:'18px 20px 10px',borderBottom:'1px solid #F1F5F9'}}>
        <div style={{fontSize:10,fontWeight:700,color:'#D97706',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>⚠ Close shift</div>
        <div style={{fontWeight:800,fontSize:16,color:'#1E293B'}}>End of shift reconciliation</div>
      </div>
      {sumQ.isLoading?<div style={{padding:28,textAlign:'center',color:'#94A3B8'}}>Loading summary…</div>:(
        <div style={{padding:'12px 20px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:7,marginBottom:12}}>
            {[{l:'Opening float',v:fmt(d?.opening_float??0),c:'#1E293B'},{l:'Cash sales',v:fmt(d?.cash_sales??0),c:'#059669'},{l:'Card sales',v:fmt(d?.card_sales??0),c:'#3B82F6'},{l:'Cash refunds',v:fmt(d?.cash_refunds??0),c:'#DC2626'},{l:'Total sales',v:fmt(d?.total_sales??0),c:'#1E293B'},{l:'Sale count',v:String(d?.sale_count??0),c:'#1E293B'}].map(item=>(
              <div key={item.l} style={{background:'#F8FAFC',borderRadius:8,padding:'8px 10px',border:'1px solid #F1F5F9'}}>
                <div style={{fontSize:10,color:'#94A3B8',fontWeight:600,marginBottom:2}}>{item.l}</div>
                <div style={{fontSize:15,fontWeight:800,color:item.c,fontFamily:'inherit'}}>{item.v}</div>
              </div>
            ))}
          </div>
          <div style={{background:'#F0FBF7',border:'1px solid #BBF7D0',borderRadius:9,padding:'10px 12px',marginBottom:10}}>
            <div style={{fontSize:11,fontWeight:700,color:'#059669',marginBottom:5}}>Expected cash in drawer</div>
            <div style={{fontWeight:900,fontSize:20,fontFamily:'inherit',color:'#1E293B'}}>{fmt(expCash)}</div>
            <div style={{fontSize:10.5,color:'#94A3B8',marginTop:2}}>Opening float + cash sales − cash refunds</div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:9}}>
            <div>
              <label className="pl3">Counted cash (declared)</label>
              <div style={{position:'relative'}}>
                <span style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:'#94A3B8',fontWeight:600,fontSize:14}}>$</span>
                <input type="number" step=".01" min="0" className="pi" style={{paddingLeft:22,fontSize:17,fontWeight:800,fontFamily:'inherit'}} value={declared} autoFocus placeholder="0.00" onChange={e=>setDeclared(e.target.value)}/>
              </div>
            </div>
            <div>
              <label className="pl3">Variance</label>
              <div style={{padding:'9px 11px',borderRadius:8,background:variance===0?'#F8FAFC':variance>0?'#F0FBF7':'#FEF2F2',border:`1.5px solid ${variance===0?'#E2E8F0':variance>0?'#BBF7D0':'#FECACA'}`,fontSize:17,fontWeight:800,fontFamily:'inherit',color:variance===0?'#1E293B':variance>0?'#059669':'#DC2626'}}>
                {declared?`${variance>=0?'+':''}${fmt(variance)}`:'—'}
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{padding:'10px 20px 16px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',display:'flex',gap:7,justifyContent:'flex-end'}}>
        <button className="sb" onClick={onClose}>Cancel</button>
        <button className="pb2" style={{background:'#DC2626',boxShadow:'none'}} disabled={!declared||loading||sumQ.isLoading} onClick={()=>onConfirm(parseFloat(declared)||0)}>{loading?'Closing…':'Close shift'}</button>
      </div>
    </div>
  </div>);
}

function EditLineModal({line,onClose,onUpdate,onRemove}:{line:CartLine;onClose:()=>void;onUpdate:(p:Partial<CartLine>)=>void;onRemove:()=>void}) {
  const [qty,setQty]=useState(line.quantity);const [price,setPrice]=useState(line.unitPrice);const [disc,setDisc]=useState(line.discountPercent);
  const lt=round2((price*qty)*(1-disc/100)*(1+line.taxRate/100));
  return(<div className="mb" onClick={onClose}><div className="mc su" onClick={e=>e.stopPropagation()} style={{maxWidth:360}}>
    <div style={{padding:'16px 18px 8px'}}>
      <div style={{fontSize:10,fontWeight:700,color:'#008060',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>Edit item</div>
      <div style={{fontWeight:800,fontSize:15,color:'#1E293B'}}>{line.name}</div>
      <div style={{fontSize:10.5,color:'#94A3B8',fontFamily:'inherit'}}>{line.sku}</div>
    </div>
    <div style={{padding:'8px 18px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
      <div><label className="pl3">Quantity</label><input type="number" min="1" className="pi" value={qty} autoFocus onChange={e=>setQty(Math.max(1,parseInt(e.target.value)||1))}/></div>
      <div><label className="pl3">Unit price</label><input type="number" step=".01" min="0" className="pi" value={price} onChange={e=>setPrice(parseFloat(e.target.value)||0)}/></div>
      <div><label className="pl3">Discount (%)</label><input type="number" step=".5" min="0" max="100" className="pi" value={disc} onChange={e=>setDisc(Math.max(0,Math.min(100,parseFloat(e.target.value)||0)))}/></div>
      <div><label className="pl3">Line total</label><div style={{padding:'9px 10px',borderRadius:8,background:'#F0FBF7',border:'1.5px solid #BBF7D0',fontWeight:800,fontSize:16,fontFamily:'inherit',color:'#008060'}}>{fmt(lt)}</div></div>
    </div>
    <div style={{padding:'8px 18px 16px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',display:'flex',gap:7,justifyContent:'flex-end'}}>
      <button className="sb" style={{color:'#DC2626',borderColor:'#FECACA',background:'#FEF2F2',marginRight:'auto'}} onClick={onRemove}>Remove</button>
      <button className="sb" onClick={onClose}>Cancel</button>
      <button className="pb2" onClick={()=>onUpdate({quantity:qty,unitPrice:price,discountPercent:disc})}>Update</button>
    </div>
  </div></div>);
}

function CartDiscountModal({current,onApply,onClose}:{current:{type:'pct'|'fixed';value:number};onApply:(d:{type:'pct'|'fixed';value:number})=>void;onClose:()=>void}) {
  const [type,setType]=useState<'pct'|'fixed'>(current.type);const [value,setValue]=useState(current.value?String(current.value):'');
  return(<div className="mb"><div className="mc su" onClick={e=>e.stopPropagation()} style={{maxWidth:320}}>
    <div style={{padding:'16px 18px 8px'}}><div style={{fontSize:10,fontWeight:700,color:'#008060',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>Cart discount · F6</div><div style={{fontWeight:800,fontSize:15,color:'#1E293B'}}>Apply to entire cart</div></div>
    <div style={{padding:'8px 18px'}}>
      <div style={{display:'flex',gap:5,marginBottom:11}}>
        {(['pct','fixed'] as const).map(t=><button key={t} onClick={()=>setType(t)} style={{flex:1,padding:'8px',borderRadius:7,border:`2px solid ${type===t?'#008060':'#E2E8F0'}`,background:type===t?'#F0FBF7':'#F8FAFC',fontWeight:700,fontSize:12,color:type===t?'#008060':'#64748B',cursor:'pointer',transition:'all .15s'}}>{t==='pct'?'% Percentage':'$ Fixed'}</button>)}
      </div>
      <label className="pl3">Discount {type==='pct'?'(%)':'amount'}</label>
      <input type="number" step={type==='pct'?'.5':'1'} min="0" max={type==='pct'?100:undefined} className="pi" value={value} placeholder="0" autoFocus onChange={e=>setValue(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')onApply({type,value:parseFloat(value)||0});}}/>
    </div>
    <div style={{padding:'8px 18px 16px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',display:'flex',gap:7,justifyContent:'flex-end'}}>
      {current.value>0&&<button className="sb" style={{color:'#DC2626',borderColor:'#FECACA',background:'#FEF2F2',marginRight:'auto'}} onClick={()=>onApply({type:'pct',value:0})}>Remove</button>}
      <button className="sb" onClick={onClose}>Cancel</button><button className="pb2" onClick={()=>onApply({type,value:parseFloat(value)||0})}>Apply</button>
    </div>
  </div></div>);
}

function ShortcutsModal({onClose}:{onClose:()=>void}) {
  const sc=[['Ctrl+B','Focus barcode'],['F2','New sale'],['F4','Customer'],['F6','Cart discount'],['F8','Hold sale'],['F9','Recall'],['F10/F12','Checkout'],['Ctrl+⇧+Z','Return/refund'],['↑↓','Navigate cart'],['Enter','Edit item'],['+ −','Qty'],['Delete','Remove'],['Ctrl+P','Price override'],['Esc','Close'],['?','Help']];
  return(<div className="mb" onClick={onClose}><div className="mc su" onClick={e=>e.stopPropagation()} style={{maxWidth:440}}>
    <div style={{padding:'16px 18px 8px'}}><div style={{fontSize:10,fontWeight:700,color:'#008060',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>Help</div><div style={{fontWeight:800,fontSize:15,color:'#1E293B'}}>Keyboard shortcuts</div></div>
    <div style={{padding:'8px 18px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,maxHeight:340,overflowY:'auto'}}>
      {sc.map(([k,l])=><div key={k} style={{display:'flex',alignItems:'center',gap:7,padding:'4px 8px',borderRadius:6,background:'#F8FAFC'}}><span style={{fontFamily:'inherit',fontWeight:700,fontSize:10.5,color:'#008060',background:'#F0FBF7',border:'1px solid #BBF7D0',borderRadius:4,padding:'1px 5px',flexShrink:0,minWidth:60,textAlign:'center'}}>{k}</span><span style={{fontSize:12,color:'#374151'}}>{l}</span></div>)}
    </div>
    <div style={{padding:'8px 18px 16px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',display:'flex',justifyContent:'flex-end'}}><button className="pb2" onClick={onClose}>Got it</button></div>
  </div></div>);
}

function ReceiptModal({receipt,onClose}:{receipt:any;onClose:()=>void}) {
  const [send,setSend]=useState<'email'|'sms'|null>(null);const [dest,setDest]=useState('');
  const sendMut=useMutation({mutationFn:()=>send==='email'?posApi.emailReceipt(receipt.saleId,dest):posApi.smsReceipt(receipt.saleId,dest),onSuccess:()=>{toast.success(`Sent to ${dest}`);setSend(null);setDest('');},onError:(e)=>toast.error(getErrorMessage(e))});
  const doPrint=()=>{
    const w=window.open('','_blank','width=400,height=640');if(!w)return;
    const cur=receipt.currencySymbol||'';
    const money=(n:number)=>`${cur}${Number(n||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`;
    const d=receipt.saleDate?new Date(receipt.saleDate):new Date();
    const dateStr=d.toLocaleDateString(undefined,{year:'numeric',month:'short',day:'numeric'})+' '+d.toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit'});
    const lines=(receipt.lines||[]).map((l:any)=>{
      const qtyPrice=`${l.quantity} × ${money(l.unitPrice)}`;
      const disc=l.discountPercent>0?` <span class="disc">(-${l.discountPercent}%)</span>`:'';
      return `<tr class="item"><td class="nm">${l.name}${disc}<div class="qp">${qtyPrice}</div></td><td class="amt">${money(l.lineTotal)}</td></tr>`;
    }).join('');
    const payRows=(receipt.payments||[]).map((p:any)=>
      `<div class="row"><span class="muted">${String(p.method).toUpperCase()}</span><span>${money(p.amount)}</span></div>`).join('');
    const totalRow=(label:string,val:string,bold=false)=>`<div class="row${bold?' tot':''}"><span>${label}</span><span>${val}</span></div>`;
    const earned=receipt.loyalty?.earnedPoints??0;
    const redeemed=receipt.loyaltyRedeemed??0;
    const html=`<!doctype html><html><head><meta charset="utf-8"><title>${receipt.receiptNumber}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'Courier New',ui-monospace,monospace;color:#111;background:#fff;padding:10px 14px;width:300px;margin:0 auto;font-size:12px;line-height:1.45;}
  .center{text-align:center;}
  .biz{font-size:17px;font-weight:800;letter-spacing:.04em;}
  .sub{font-size:10.5px;color:#444;margin-top:2px;}
  .meta{margin:8px 0;font-size:11px;}
  .meta .row{display:flex;justify-content:space-between;}
  .muted{color:#555;}
  hr{border:none;border-top:1px dashed #999;margin:8px 0;}
  table{width:100%;border-collapse:collapse;}
  td{vertical-align:top;padding:3px 0;}
  .nm{font-size:11.5px;}
  .qp{font-size:10px;color:#666;}
  .amt{text-align:right;font-size:11.5px;white-space:nowrap;padding-left:8px;}
  .disc{color:#c00;font-size:9.5px;}
  .row{display:flex;justify-content:space-between;font-size:11.5px;padding:1px 0;}
  .tot{font-size:15px;font-weight:800;border-top:2px solid #111;border-bottom:2px solid #111;padding:5px 0;margin:5px 0;}
  .change{background:#f3f4f6;border-radius:6px;padding:5px 8px;margin-top:6px;font-weight:700;}
  .loy{margin-top:6px;font-size:10.5px;color:#0a7;text-align:center;}
  .foot{margin-top:12px;font-size:10.5px;color:#555;}
  .barcode{font-family:'Libre Barcode 39',monospace;font-size:34px;letter-spacing:2px;margin-top:6px;}
  @media print{body{width:auto;padding:0 4px;}@page{margin:6mm;}}
</style></head>
<body>
  <div class="center">
    <div class="biz">OmniRetail</div>
    <div class="sub">Point of Sale Receipt</div>
  </div>
  <div class="meta">
    <div class="row"><span class="muted">Receipt</span><span>${receipt.receiptNumber}</span></div>
    <div class="row"><span class="muted">Date</span><span>${dateStr}</span></div>
    ${receipt.cashierName?`<div class="row"><span class="muted">Cashier</span><span>${receipt.cashierName}</span></div>`:''}
    ${receipt.terminalCode?`<div class="row"><span class="muted">Terminal</span><span>${receipt.terminalCode}</span></div>`:''}
    ${receipt.customerName?`<div class="row"><span class="muted">Customer</span><span>${receipt.customerName}</span></div>`:''}
  </div>
  <hr/>
  <table>${lines}</table>
  <hr/>
  ${totalRow('Subtotal',money(receipt.subtotal))}
  ${receipt.discountTotal>0?totalRow('Discount',`-${money(receipt.discountTotal)}`):''}
  ${totalRow('Tax',money(receipt.taxTotal))}
  ${totalRow('TOTAL',money(receipt.grandTotal),true)}
  ${payRows}
  ${receipt.changeDue>0?`<div class="row change"><span>CHANGE</span><span>${money(receipt.changeDue)}</span></div>`:''}
  ${(earned>0||redeemed>0)?`<div class="loy">${earned>0?`★ Earned ${earned} points`:''}${earned>0&&redeemed>0?' · ':''}${redeemed>0?`Redeemed ${redeemed} points`:''}</div>`:''}
  <hr/>
  <div class="center foot">
    Thank you for shopping with us!<br/>
    Items: ${(receipt.lines||[]).length} &nbsp;•&nbsp; ${dateStr.split(' ').slice(-1)[0]}<br/>
    <div style="margin-top:6px;font-size:10px;">${receipt.receiptNumber}</div>
  </div>
</body></html>`;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(()=>{try{w.print();}catch(e){}},250);
  };
  useEffect(()=>{const h=(e:globalThis.KeyboardEvent)=>{if(e.key==='Escape')onClose();if((e.ctrlKey||e.metaKey)&&e.key.toLowerCase()==='e'){e.preventDefault();setSend('email');}if((e.ctrlKey||e.metaKey)&&e.key.toLowerCase()==='p'){e.preventDefault();doPrint();}};window.addEventListener('keydown',h);return()=>window.removeEventListener('keydown',h);},[send]);
  return(<div className="mb" onClick={onClose}><div className="mc su" onClick={e=>e.stopPropagation()}>
    <div style={{padding:'24px 20px 16px',textAlign:'center',background:'linear-gradient(to bottom,#F0FBF7,#fff)'}}>
      <div style={{width:50,height:50,borderRadius:'50%',background:'#008060',color:'#fff',margin:'0 auto 10px',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 8px 20px rgba(0,128,96,.30)'}}><CheckIcon/></div>
      <div style={{fontSize:10,fontWeight:700,color:'#059669',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Sale complete</div>
      <div style={{fontWeight:900,fontSize:28,color:'#1E293B',fontFamily:'inherit',letterSpacing:'-.03em'}}>{fmt(receipt.grandTotal)}</div>
      <div style={{fontSize:11,color:'#94A3B8',fontFamily:'inherit',marginTop:3}}>{receipt.receiptNumber}</div>
      {receipt.changeDue>0&&<div style={{marginTop:9,padding:'5px 14px',background:'#F0FBF7',border:'1px solid #BBF7D0',borderRadius:99,display:'inline-block',fontSize:13,fontWeight:700,color:'#008060'}}>💵 Change: {fmt(receipt.changeDue)}</div>}
    </div>
    <div style={{padding:'10px 20px',borderTop:'1px solid #F1F5F9'}}>
      {[['Subtotal',fmt(receipt.subtotal)],...(receipt.discountTotal>0?[['Discount',`−${fmt(receipt.discountTotal)}`]]:[] as any),['Tax',fmt(receipt.taxTotal)],['Paid',fmt(receipt.paidTotal)]].map(([l,v]:any)=>(
        <div key={l} style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#64748B',padding:'2px 0'}}><span>{l}</span><span style={{fontFamily:'inherit',fontWeight:600}}>{v}</span></div>
      ))}
    </div>
    {receipt.loyalty?.earnedPoints>0&&<div style={{margin:'0 20px 10px',padding:'7px 11px',background:'#F0FBF7',border:'1px solid #BBF7D0',borderRadius:8,fontSize:12,color:'#059669',fontWeight:600}}>★ Earned {receipt.loyalty.earnedPoints} loyalty pts</div>}
    {send?(<div style={{padding:'10px 20px 16px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9'}}>
      <label className="pl3">{send==='email'?'Email address':'Phone number'}</label>
      <input autoFocus className="pi" type={send==='email'?'email':'tel'} value={dest} onChange={e=>setDest(e.target.value)} placeholder={send==='email'?'customer@email.com':'+1 555 000 0000'} style={{marginBottom:9}} onKeyDown={e=>{if(e.key==='Enter'&&dest)sendMut.mutate();}}/>
      <div style={{display:'flex',gap:7}}><button className="sb" style={{flex:1}} onClick={()=>{setSend(null);setDest('');}}>Cancel</button><button className="pb2" style={{flex:1}} disabled={!dest||sendMut.isPending} onClick={()=>sendMut.mutate()}>{sendMut.isPending?'Sending…':'Send'}</button></div>
    </div>):(<div style={{padding:'10px 20px 16px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9'}}>
      <div style={{fontSize:10.5,fontWeight:600,color:'#94A3B8',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:7}}>Receipt options</div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5,marginBottom:7}}>
        <button className="sb" style={{fontSize:12}} onClick={doPrint}>🖨 Print <span style={{fontSize:9.5,color:'#94A3B8'}}>Ctrl+P</span></button>
        <button className="sb" style={{fontSize:12}} onClick={()=>setSend('email')}>📧 Email <span style={{fontSize:9.5,color:'#94A3B8'}}>Ctrl+E</span></button>
        <button className="sb" style={{fontSize:12}} onClick={()=>setSend('sms')}>📱 SMS</button>
        <button className="sb" style={{fontSize:12}} onClick={()=>window.open(`https://wa.me/?text=Receipt ${receipt.receiptNumber} - Total: ${fmt(receipt.grandTotal)}`,'_blank')}>💬 WhatsApp</button>
      </div>
      <button className="pb2" style={{width:'100%'}} onClick={onClose}>Next sale → <span style={{fontSize:10.5,opacity:.7}}>Esc</span></button>
    </div>)}
  </div></div>);
}

function HoldCartButton({cart,terminalCode,onHeld}:{cart:CartLine[];terminalCode:string;onHeld:()=>void}) {
  const [open,setOpen]=useState(false);const [ref,setRef]=useState('');
  const mut=useMutation({mutationFn:()=>posApi.holdCart({terminalCode,holdReference:ref||`H-${Date.now().toString().slice(-6)}`,cartData:{lines:cart},expiresInMinutes:720}),onSuccess:d=>{toast.success(`Held as ${d.holdReference}`);setOpen(false);setRef('');onHeld();},onError:(e)=>toast.error(getErrorMessage(e))});
  if(cart.length===0)return<button id="hbtn" style={{display:'none'}}/>;
  if(!open)return<button id="hbtn" style={{fontSize:11,fontWeight:600,color:'#94A3B8',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.10)',borderRadius:7,padding:'4px 8px',cursor:'pointer'}} onClick={()=>setOpen(true)}>⏸ F8</button>;
  return(<div style={{display:'flex',alignItems:'center',gap:5,background:'rgba(255,255,255,.08)',border:'1px solid rgba(255,255,255,.12)',borderRadius:9,padding:'3px 6px 3px 3px'}}>
    <input autoFocus value={ref} onChange={e=>setRef(e.target.value)} placeholder="Hold reference" onKeyDown={e=>{if(e.key==='Enter')mut.mutate();if(e.key==='Escape')setOpen(false);}} style={{width:130,padding:'3px 7px',borderRadius:5,border:'1px solid rgba(255,255,255,.15)',fontSize:11.5,outline:'none',background:'rgba(255,255,255,.1)',color:'#fff'}}/>
    <button style={{background:'#008060',color:'#fff',border:'none',borderRadius:5,padding:'3px 8px',fontSize:11,fontWeight:700,cursor:'pointer'}} disabled={mut.isPending} onClick={()=>mut.mutate()}>Hold</button>
    <button style={{background:'none',border:'none',cursor:'pointer',color:'#64748B',display:'flex'}} onClick={()=>setOpen(false)}><XIcon/></button>
  </div>);
}

function ResumeCartMenu({terminalCode,onResumed}:{terminalCode:string;onResumed:(d:any)=>void}) {
  const qc=useQueryClient();const [open,setOpen]=useState(false);
  const heldQ=useQuery({queryKey:['held-carts',terminalCode],queryFn:()=>posApi.listHeldCarts({terminalCode}),enabled:open});
  const mut=useMutation({mutationFn:async(ref:string)=>{const g=await posApi.getHeldCart(ref);await posApi.releaseHeldCart(ref);return g.cartData;},onSuccess:d=>{onResumed(d);setOpen(false);qc.invalidateQueries({queryKey:['held-carts']});},onError:(e)=>toast.error(getErrorMessage(e))});
  const count=heldQ.data?.length??0;
  return(<div style={{position:'relative'}}>
    <button id="rbtn" style={{fontSize:11,fontWeight:600,color:'#94A3B8',background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.10)',borderRadius:7,padding:'4px 8px',cursor:'pointer'}} onClick={()=>setOpen(v=>!v)}>▶ F9{count>0?` (${count})`:''}</button>
    {open&&<div style={{position:'absolute',left:0,top:'calc(100%+6px)',width:240,background:'#fff',border:'1px solid #E2E8F0',borderRadius:11,boxShadow:'0 8px 24px rgba(0,0,0,.12)',overflow:'hidden',zIndex:50}}>
      <div style={{padding:'7px 11px',borderBottom:'1px solid #F1F5F9',fontSize:10,fontWeight:700,color:'#94A3B8',textTransform:'uppercase',letterSpacing:'.04em'}}>Held sales</div>
      {heldQ.isLoading?<div style={{padding:'11px',fontSize:12,color:'#94A3B8'}}>Loading…</div>
       :!count?<div style={{padding:'11px',fontSize:12,color:'#94A3B8'}}>No held carts.</div>
       :heldQ.data!.map((h:any)=><button key={h.id} disabled={mut.isPending} onClick={()=>mut.mutate(h.hold_reference)} style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 11px',border:'none',background:'#fff',cursor:'pointer',borderBottom:'1px solid #F8FAFC',textAlign:'left',transition:'background .1s'}} onMouseEnter={e=>e.currentTarget.style.background='#F8FAFC'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
          <div><div style={{fontFamily:'inherit',fontWeight:700,color:'#1E293B',fontSize:12}}>{h.hold_reference}</div><div style={{fontSize:10.5,color:'#94A3B8'}}>{h.line_count} item{h.line_count===1?'':'s'}</div></div>
          <div style={{fontSize:10,color:'#94A3B8'}}>{new Date(h.created_at).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</div>
        </button>)}
    </div>}
  </div>);
}

export function PriceOverrideModal({terminalCode,productId,currentPrice,onApproved,onClose}:{terminalCode:string;productId:string;currentPrice:number;onApproved:(p:number)=>void;onClose:()=>void}) {
  const [np,setNp]=useState(currentPrice);const [reason,setReason]=useState('PROMO');const [mgEmail,setMgEmail]=useState('manager@omniretail.local');const [mgPw,setMgPw]=useState('');
  const delta=np-currentPrice,deltaPct=currentPrice>0?(delta/currentPrice)*100:0;
  const mut=useMutation({
    mutationFn:async()=>{const auth=await authApi.login(mgEmail,mgPw);if(!auth?.user?.permissions?.includes('pos.price_override.approve')&&!auth?.user?.permissions?.includes('*'))throw new Error('No permission');await axios.post(`${apiBaseUrl}/pos/price-overrides`,{terminalCode,productId,oldPrice:currentPrice,newPrice:np,reasonCode:reason},{headers:{Authorization:`Bearer ${auth.accessToken}`}});try{await axios.post(`${apiBaseUrl}/auth/logout`,{refreshToken:auth.refreshToken});}catch{}},
    onSuccess:()=>{toast.success('Override authorized');onApproved(np);},onError:(e)=>toast.error(getErrorMessage(e)),
  });
  const ok=np>=0&&np!==currentPrice&&mgEmail&&mgPw;
  return(<div className="mb" onClick={onClose}><div className="mc su" onClick={e=>e.stopPropagation()} style={{maxWidth:400}}>
    <div style={{padding:'16px 18px 8px'}}><div style={{fontSize:10,fontWeight:700,color:'#D97706',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>⚠ Manager auth</div><div style={{fontWeight:800,fontSize:15,color:'#1E293B'}}>Price override</div></div>
    <div style={{padding:'8px 18px',display:'flex',flexDirection:'column',gap:10}}>
      <div style={{background:'#F8FAFC',borderRadius:9,padding:11,display:'flex',alignItems:'center',gap:9}}>
        <div><div style={{fontSize:9.5,color:'#94A3B8',fontWeight:600,marginBottom:2}}>Current</div><div style={{fontWeight:800,fontSize:17,fontFamily:'inherit',color:'#64748B'}}>{fmt(currentPrice)}</div></div>
        <div style={{color:'#94A3B8',fontSize:15}}>→</div>
        <div style={{flex:1}}><div style={{fontSize:9.5,color:'#94A3B8',fontWeight:600,marginBottom:3}}>New</div><input type="number" step=".01" min="0" className="pi" value={np} autoFocus onChange={e=>setNp(parseFloat(e.target.value)||0)} style={{fontWeight:800,fontSize:16,fontFamily:'inherit'}}/></div>
        {np!==currentPrice&&<div style={{fontSize:11.5,fontWeight:700,color:delta<0?'#059669':'#DC2626',flexShrink:0}}>{delta<0?'↓':'↑'}{fmt(Math.abs(delta))} ({deltaPct>0?'+':''}{deltaPct.toFixed(1)}%)</div>}
      </div>
      <div><label className="pl3">Reason</label><select className="pi" value={reason} onChange={e=>setReason(e.target.value)}><option value="PROMO">Promotion</option><option value="DAMAGED">Damaged</option><option value="PRICE_MATCH">Price match</option><option value="LOYALTY">Loyalty</option><option value="OTHER">Other</option></select></div>
      <div><label className="pl3">Manager email</label><input type="email" className="pi" value={mgEmail} onChange={e=>setMgEmail(e.target.value)}/></div>
      <div><label className="pl3">Manager password</label><input type="password" className="pi" value={mgPw} onChange={e=>setMgPw(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&ok)mut.mutate();}}/></div>
    </div>
    <div style={{padding:'8px 18px 16px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',display:'flex',gap:7,justifyContent:'flex-end'}}>
      <button className="sb" onClick={onClose}>Cancel</button>
      <button className="pb2" disabled={!ok||mut.isPending} onClick={()=>mut.mutate()}>{mut.isPending?'Authorizing…':'Authorize'}</button>
    </div>
  </div></div>);
}

function CustomerPicker({selected,onSelect,forceOpen,onClose}:{selected:{id:string;name:string}|null;onSelect:(c:{id:string;name:string}|null)=>void;forceOpen?:boolean;onClose?:()=>void}) {
  const [query,setQuery]=useState('');
  const [open,setOpen]=useState(false);
  const [hiIdx,setHiIdx]=useState(-1);
  const inputRef=useRef<HTMLInputElement>(null);
  const listRef=useRef<HTMLDivElement>(null);

  useEffect(()=>{if(forceOpen){setOpen(true);setTimeout(()=>inputRef.current?.focus(),50);}},[forceOpen]);
  // reset highlight when results change
  const results=useQuery({queryKey:['sales','cs',query],queryFn:()=>salesApi.listCustomers({q:query,limit:15}),enabled:open&&query.length>=1});
  useEffect(()=>setHiIdx(-1),[query]);

  const rows:any[]=results.data??[];

  const confirm=(c:{id:string;name:string})=>{onSelect(c);setQuery('');setOpen(false);setHiIdx(-1);};

  const handleKey=(e:React.KeyboardEvent<HTMLInputElement>)=>{
    if(!open||rows.length===0) return;
    if(e.key==='ArrowDown'){
      e.preventDefault();
      const next=Math.min(hiIdx+1,rows.length-1);
      setHiIdx(next);
      // scroll highlighted row into view
      const el=listRef.current?.children[next] as HTMLElement|undefined;
      el?.scrollIntoView({block:'nearest'});
    } else if(e.key==='ArrowUp'){
      e.preventDefault();
      const next=Math.max(hiIdx-1,0);
      setHiIdx(next);
      const el=listRef.current?.children[next] as HTMLElement|undefined;
      el?.scrollIntoView({block:'nearest'});
    } else if(e.key==='Enter'){
      e.preventDefault();
      if(hiIdx>=0&&rows[hiIdx])confirm({id:rows[hiIdx].id,name:rows[hiIdx].name});
      else if(rows.length===1)confirm({id:rows[0].id,name:rows[0].name});
    } else if(e.key==='Escape'){
      setOpen(false);setQuery('');setHiIdx(-1);onClose?.();
    }
  };

  const initials=selected?selected.name.split(/\s+/).filter(Boolean).slice(0,2).map(s=>s[0]?.toUpperCase()||'').join('')||'?':'';
  if(selected)return(<div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 9px',background:'#F0FBF7',borderRadius:9,border:'1px solid #BBF7D0'}}>
    <div style={{width:30,height:30,borderRadius:'50%',background:'#008060',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:11,flexShrink:0}}>{initials}</div>
    <div style={{flex:1,minWidth:0}}><div style={{fontSize:9.5,color:'#059669',fontWeight:700,textTransform:'uppercase',letterSpacing:'.04em'}}>Customer · F4</div><div style={{fontWeight:700,color:'#1E293B',fontSize:12.5}}>{selected.name}</div></div>
    <button onClick={()=>onSelect(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#94A3B8',display:'flex'}}><XIcon/></button>
  </div>);
  return(<div style={{position:'relative'}}>
    <div style={{position:'relative'}}>
      <div style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:'#94A3B8',pointerEvents:'none'}}><UserIcon/></div>
      <input ref={inputRef} className="pi" style={{paddingLeft:28,fontSize:12.5}}
        placeholder="Search customer… (F4)"
        value={query}
        onChange={e=>{setQuery(e.target.value);setOpen(true);setHiIdx(-1);}}
        onFocus={()=>setOpen(true)}
        onBlur={()=>setTimeout(()=>{setOpen(false);onClose?.();},150)}
        onKeyDown={handleKey}
      />
    </div>
    {open&&query.length>=1&&<div ref={listRef} style={{position:'absolute',left:0,right:0,top:'calc(100% + 4px)',background:'#fff',border:'1px solid #E2E8F0',borderRadius:9,boxShadow:'0 8px 24px rgba(0,0,0,.10)',overflow:'hidden',maxHeight:220,overflowY:'auto',zIndex:30}}>
      {results.isLoading?<div style={{padding:'9px 11px',fontSize:12,color:'#94A3B8'}}>Searching…</div>
       :!rows.length?<div style={{padding:'9px 11px',fontSize:12,color:'#94A3B8'}}>No customers found</div>
       :rows.map((c:any,i:number)=>{
         const isHi=i===hiIdx;
         return(<button key={c.id} onMouseDown={e=>e.preventDefault()} onClick={()=>confirm({id:c.id,name:c.name})}
           onMouseEnter={()=>setHiIdx(i)}
           style={{width:'100%',display:'flex',alignItems:'center',gap:8,padding:'8px 11px',border:'none',
             background:isHi?'#F0FBF7':'#fff',
             borderLeft:isHi?'3px solid #008060':'3px solid transparent',
             cursor:'pointer',borderBottom:'1px solid #F8FAFC',textAlign:'left',transition:'background .08s'}}>
           <div style={{width:27,height:27,borderRadius:'50%',
             background:isHi?'#D1FAE5':'#F1F5F9',
             color:isHi?'#008060':'#64748B',
             display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:11,flexShrink:0}}>
             {c.name.trim()[0]?.toUpperCase()||'?'}
           </div>
           <div>
             <div style={{fontWeight:600,color:'#1E293B',fontSize:12.5}}>{c.name}</div>
             <div style={{fontSize:10.5,color:'#94A3B8'}}>{c.phone??c.email??'—'}</div>
           </div>
           {isHi&&<div style={{marginLeft:'auto',fontSize:10,color:'#008060',fontWeight:700,opacity:.7}}>↵ Select</div>}
         </button>);
       })}
      <div style={{padding:'5px 11px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',display:'flex',gap:10,fontSize:10,color:'#94A3B8'}}>
        <span><span style={{fontWeight:700,color:'#64748B'}}>↑↓</span> navigate</span>
        <span><span style={{fontWeight:700,color:'#64748B'}}>↵</span> select</span>
        <span><span style={{fontWeight:700,color:'#64748B'}}>Esc</span> close</span>
      </div>
    </div>}
  </div>);
}

function LoyaltyPanel({customerId,grandTotalBeforeRedemption,pointsToRedeem,onChangePoints}:{customerId:string;grandTotalBeforeRedemption:number;pointsToRedeem:number;onChangePoints:(n:number)=>void}) {
  const statusQ=useQuery({queryKey:['pos','ls',customerId],queryFn:()=>posApi.getLoyaltyStatus(customerId)});
  const previewQ=useQuery({queryKey:['pos','lp',customerId,grandTotalBeforeRedemption,pointsToRedeem],queryFn:()=>posApi.previewRedemption(customerId,grandTotalBeforeRedemption,pointsToRedeem),enabled:pointsToRedeem>0&&grandTotalBeforeRedemption>0});
  if(!statusQ.data)return null;
  const{pointsBalance,valueOfPoints,config}=statusQ.data;
  const maxR=Math.min(pointsBalance,Math.floor((grandTotalBeforeRedemption*(config.maxRedeemPctPerSale/100))/config.redeemValuePerPoint));
  return(<div style={{background:'#FFFBEB',borderRadius:7,padding:'7px 9px',border:'1px solid #FDE68A'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
      <div style={{display:'flex',alignItems:'center',gap:4}}><span style={{color:'#D97706'}}>★</span><span style={{fontSize:11.5,fontWeight:700,color:'#92400E'}}>{pointsBalance.toLocaleString()} pts · {fmt(valueOfPoints)}</span></div>
      {pointsToRedeem>0&&<button style={{fontSize:10.5,color:'#DC2626',background:'none',border:'none',cursor:'pointer',fontWeight:700}} onClick={()=>onChangePoints(0)}>Clear</button>}
    </div>
    {maxR>=config.minRedeemPoints?<div style={{display:'flex',alignItems:'center',gap:5}}><input type="range" min={0} max={maxR} step={config.minRedeemPoints} value={pointsToRedeem} onChange={e=>onChangePoints(parseInt(e.target.value,10))} style={{flex:1,accentColor:'#D97706'}}/><input type="number" min={0} max={maxR} value={pointsToRedeem} onChange={e=>onChangePoints(Math.max(0,Math.min(maxR,parseInt(e.target.value)||0)))} style={{width:55,padding:'2px 5px',borderRadius:5,border:'1px solid #FDE68A',fontSize:11,textAlign:'right',outline:'none',fontFamily:'inherit',background:'#fff'}}/></div>
     :<div style={{fontSize:11,color:'#B45309'}}>{pointsBalance<config.minRedeemPoints?`Need ${config.minRedeemPoints} pts`:'Sale too small'}</div>}
    {pointsToRedeem>0&&previewQ.data?.ok&&<div style={{fontSize:11,color:'#059669',marginTop:4,fontWeight:600}}>Discount: {fmt(previewQ.data.requestedDollars)} · {previewQ.data.pointsBalanceAfter.toLocaleString()} pts remaining</div>}
  </div>);
}
